export default EventStack = (() => {
  // Store event stacks by element and event type
  const eventStacks = new Map();

  /**
   * Ensure an event stack exists for a given element and event type
   * @param {Element|Window} element - The target element for the event
   * @param {string} eventType - The event type (e.g., "resize", "click")
   */
  const ensureEventStack = (element, eventType) => {
    const key = `${element}_${eventType}`;

    if (!eventStacks.has(key)) {
      // Create a new stack for the event
      const callbackStack = [];

      // Event handler to invoke all callbacks in the stack
      const handler = (event) => {
        callbackStack.forEach((callback) => callback(event));
      };

      // Attach the event handler once
      element.addEventListener(eventType, handler);

      // Store the stack and handler for cleanup later
      eventStacks.set(key, { callbackStack, handler });
    }

    return eventStacks.get(key).callbackStack;
  };

  /**
   * Add an event listener callback to the stack
   * @param {Element|Window} element - Target element
   * @param {string} eventType - Event type
   * @param {function} callback - Function to invoke on event
   */
  const on = (element, eventType, callback) => {
    const stack = ensureEventStack(element, eventType);
    stack.push(callback);
  };

  /**
   * Remove a specific event listener callback from the stack
   * @param {Element|Window} element - Target element
   * @param {string} eventType - Event type
   * @param {function} callback - Function to remove
   */
  const off = (element, eventType, callback) => {
    const key = `${element}_${eventType}`;
    if (eventStacks.has(key)) {
      const { callbackStack } = eventStacks.get(key);
      const index = callbackStack.indexOf(callback);
      if (index !== -1) {
        callbackStack.splice(index, 1);
      }
    }
  };

  /**
   * Clear all event listeners for a given element and event type
   * @param {Element|Window} element - Target element
   * @param {string} eventType - Event type
   */
  const clear = (element, eventType) => {
    const key = `${element}_${eventType}`;
    if (eventStacks.has(key)) {
      const { handler } = eventStacks.get(key);
      element.removeEventListener(eventType, handler);
      eventStacks.delete(key);
    }
  };

  return {
    on,
    off,
    clear,
  };
})();
